// features/projects/templates/professional/dashboard.js
// ═══════════════════════════════════════════════════════════
// PROFESSIONAL DASHBOARD — slot layout
//
// Editorial-style structure inspired by the Compound dashboard
// reference (locked with Tim 2026-05-03):
//
//   Pulse Hero (auto-synthesized headline + stat strip)
//   Row 1: 3-card snapshot (1/3 each)
//   Row 2: featured spotlight (2/3) + supporting card (1/3)
//   Row 3: 3-card action row (1/3 each)
//   Row 4: 2-card footer (1/2 each)
//
// Slots are fixed; each slot has a default widget. The previous
// democratic widget-grid (drag-reorder + add/remove) is retired
// in favor of this curated structure. Per-slot swap UX is
// flagged as a follow-up — for v1 the layout is the design.
// ═══════════════════════════════════════════════════════════

import { L, FONT } from './tokens';
import { DashboardDataProvider } from './widgets/data-provider';

import PulseHero from './widgets/pulse-hero';
import FindingsStatusWidget from './widgets/findings-status';
import PhaseACoverageWidget from './widgets/phase-a-coverage';
import TrackRecordWidget from './widgets/track-record';
import StrongestThreadsWidget from './widgets/strongest-threads';
import ConfidenceMovementWidget from './widgets/confidence-movement';
import PathForwardWidget from './widgets/path-forward';
import WhereThingsBreakWidget from './widgets/where-things-break';
import SolutionChainsWidget from './widgets/solution-chains';
import WhatWeDontKnowWidget from './widgets/what-we-dont-know';
import SimulatorSnapshotWidget from './widgets/simulator-snapshot';

export default function ProfessionalDashboard({ workspaceId, projectName }) {
  return (
    <div style={{
      height: '100%', background: 'transparent', color: L.text,
      fontFamily: FONT.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header />
      <div style={{
        flex: 1, minHeight: 0, overflow: 'auto',
        padding: '24px 32px 80px 32px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <DashboardDataProvider workspaceId={workspaceId}>
            <SlotLayout projectName={projectName} />
          </DashboardDataProvider>
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${L.b1}`, background: L.s1, flexShrink: 0, gap: 14,
      boxShadow: `inset 0 -1px 0 0 ${L.brass}20`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 3,
        background: `${L.brass}15`, border: `1px solid ${L.brass}40`,
        color: L.brass, letterSpacing: '0.06em',
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: L.brass }} />
        Dashboard — Synthesis Briefing
      </div>
      <div style={{ fontSize: 10, color: L.silverOxide, fontFamily: FONT.serif, fontStyle: 'italic' }}>
        compose a synthesis · each widget answers one research question
      </div>
    </div>
  );
}

// ─── Slot layout ──────────────────────────────────────────────
// Fixed structure of slots. Each slot renders its assigned widget.
// Sizing is enforced via CSS grid templates per-row.
function SlotLayout({ projectName }) {
  return (
    <>
      {/* PULSE HERO — full width, the editorial anchor */}
      <PulseHero projectName={projectName} />

      {/* ROW 1 — 3-card snapshot */}
      <Row columns="1fr 1fr 1fr">
        <FindingsStatusWidget />
        <PhaseACoverageWidget />
        <TrackRecordWidget />
      </Row>

      {/* ROW 2 — featured spotlight + supporting card */}
      <Row columns="2fr 1fr">
        <StrongestThreadsWidget featured />
        <ConfidenceMovementWidget />
      </Row>

      {/* ROW 3 — action row */}
      <Row columns="1fr 1fr 1fr">
        <PathForwardWidget featured />
        <WhereThingsBreakWidget />
        <SolutionChainsWidget />
      </Row>

      {/* ROW 4 — footer */}
      <Row columns="1fr 1fr">
        <WhatWeDontKnowWidget />
        <SimulatorSnapshotWidget />
      </Row>
    </>
  );
}

function Row({ columns, children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: columns,
      gap: 18,                  // matches wealth-building wb-grid gap
      marginBottom: 18,         // matches wealth-building wb-grid margin-bottom
      alignItems: 'stretch',    // explicit equal-height across row
    }}>
      {children}
    </div>
  );
}
